/**
 * SigningScreen — The page signers see when they click the email link.
 * Works for both authenticated users and external signers (via token).
 * Route: /sign/:requestId?token=...
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const BG    = '#f8fafc';
const BURG  = '#800020';
const DARK  = '#1e293b';
const MUTED = '#64748b';
const BORD  = '#e2e8f0';

// ─── Draw Tab ─────────────────────────────────────────────
function DrawTab({ onCapture, clearLabel }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    return {
      x: ((touch?.clientX ?? e.clientX) - rect.left) * (canvas.width / rect.width),
      y: ((touch?.clientY ?? e.clientY) - rect.top)  * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    lastPos.current = pos;
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };
  const stop = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onCapture(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onCapture(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={480} height={140}
        style={{ width: '100%', height: 140, border: `1px solid ${BORD}`, borderRadius: 10, background: '#fff', cursor: 'crosshair', touchAction: 'none' }}
        onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
      />
      <button onClick={clear} style={{ marginTop: 8, fontSize: 12, color: MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        {clearLabel}
      </button>
    </div>
  );
}

// ─── Type Tab ─────────────────────────────────────────────
function TypeTab({ onCapture, placeholder }) {
  const [text, setText] = useState('');
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!text.trim()) { onCapture(null); return; }
    ctx.font = `italic 40px 'Georgia', serif`;
    ctx.fillStyle = '#1e293b';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    onCapture(canvas.toDataURL('image/png'));
  }, [text, onCapture]);

  return (
    <div>
      <input
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 14px', border: `1px solid ${BORD}`, borderRadius: 10,
          fontSize: 16, fontStyle: 'italic', fontFamily: "'Georgia', serif",
          outline: 'none', boxSizing: 'border-box', marginBottom: 10,
        }}
      />
      <canvas
        ref={canvasRef}
        width={480} height={100}
        style={{ width: '100%', height: 100, border: `1px solid ${BORD}`, borderRadius: 10, background: '#fff', display: text ? 'block' : 'none' }}
      />
    </div>
  );
}

// ─── Upload Tab ───────────────────────────────────────────
function UploadTab({ onCapture, ctaLabel }) {
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onCapture(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <label style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: 120, border: `2px dashed ${BORD}`, borderRadius: 10, cursor: 'pointer', background: '#fff',
    }}>
      <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
      <span style={{ fontSize: 13, color: MUTED, marginTop: 8 }}>{ctaLabel}</span>
      <input type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
    </label>
  );
}

// ─── Main ─────────────────────────────────────────────────
const SigningScreen = () => {
  const { t } = useTranslation();
  const { requestId } = useParams();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  const [phase, setPhase] = useState('loading'); // loading | ready | signed | declined | error
  const [request, setRequest] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [sigTab, setSigTab] = useState('draw');
  const [sigData, setSigData] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);

  // Load document
  useEffect(() => {
    if (!token) { setPhase('error'); setErrorMsg(t('screens.signing.noToken')); return; }
    axios.get(`${BASE_URL}/signatures/${requestId}/view?token=${token}`)
      .then(res => {
        setRequest(res.data.data.request);
        setDocUrl(res.data.data.url);
        setPhase('ready');
      })
      .catch(e => {
        setPhase('error');
        setErrorMsg(e.response?.data?.message || t('screens.signing.loadFailed'));
      });
  }, [requestId, token, t]);

  const handleSign = async () => {
    if (!sigData)         { alert(t('screens.signing.alertProvideSig')); return; }
    if (!consentChecked)  { alert(t('screens.signing.alertConsent')); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/signatures/${requestId}/sign?token=${token}`, {
        signatureData: sigData,
        signatureMethod: sigTab,
        consentAccepted: true,
      });
      setPhase('signed');
    } catch (e) {
      alert(e.response?.data?.message || t('screens.signing.signFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) { alert(t('screens.signing.alertReason')); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/signatures/${requestId}/decline?token=${token}`, { reason: declineReason });
      setPhase('declined');
    } catch (e) {
      alert(e.response?.data?.message || t('screens.signing.declineFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const captureCallback = useCallback((data) => setSigData(data), []);

  // ── Render ────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif" }}>
        <div style={{ color: MUTED, fontSize: 15 }}>{t('screens.signing.loading')}</div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 480, textAlign: 'center', border: `1px solid ${BORD}` }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: DARK, marginBottom: 8 }}>{t('screens.signing.unableToLoad')}</div>
          <div style={{ fontSize: 14, color: MUTED }}>{errorMsg}</div>
        </div>
      </div>
    );
  }

  if (phase === 'signed') {
    const allSigned = request?.signers?.every(s => s.status === 'signed' || s.email === request?.signerEmail);
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 520, textAlign: 'center', border: `1px solid ${BORD}` }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: DARK, marginBottom: 8 }}>{t('screens.signing.signedTitle')}</div>
          <div style={{ fontSize: 14, color: MUTED, marginBottom: 24 }}>
            {t('screens.signing.signedBody')} <strong>{request?.title}</strong>.
            {allSigned
              ? t('screens.signing.signedComplete')
              : t('screens.signing.signedWaiting')}
          </div>
          <div style={{ fontSize: 12, color: MUTED, padding: '12px 16px', background: BG, borderRadius: 10, border: `1px solid ${BORD}`, textAlign: 'left', lineHeight: 1.6 }}>
            {t('screens.signing.signedRecordNote')}
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'declined') {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 480, textAlign: 'center', border: `1px solid ${BORD}` }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: DARK, marginBottom: 8 }}>{t('screens.signing.declinedTitle')}</div>
          <div style={{ fontSize: 14, color: MUTED }}>{t('screens.signing.declinedBody')}</div>
        </div>
      </div>
    );
  }

  // ── Main signing UI ───────────────────────────────────────
  const pendingCount = request?.signers?.filter(s => s.status === 'pending' || s.status === 'viewed').length - 1;

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif", color: DARK }}>

      {/* Top bar */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${BORD}`, padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: BURG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
            </svg>
          </div>
          <span style={{ fontWeight: 700, fontSize: 15, color: DARK }}>{t('screens.signing.topBarBrand')}</span>
        </div>
        <div style={{ fontSize: 12, color: MUTED }}>
          {t('screens.signing.governedBy')}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>

        {/* Doc info */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: `1px solid ${BORD}`, marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{t('screens.signing.docToSign')}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: DARK, marginBottom: 4 }}>{request?.title}</div>
          {request?.description && <div style={{ fontSize: 14, color: MUTED, marginBottom: 8 }}>{request.description}</div>}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 10 }}>
            {request?.expiresAt && (
              <div style={{ fontSize: 13, color: new Date(request.expiresAt) < new Date() ? '#ef4444' : MUTED }}>
                <strong style={{ color: DARK }}>{t('screens.signing.expires')}</strong>{' '}
                {formatDate(request.expiresAt)}
              </div>
            )}
            {pendingCount > 0 && (
              <div style={{ fontSize: 13, color: MUTED }}>
                <strong style={{ color: DARK }}>{pendingCount}</strong> {pendingCount !== 1 ? t('screens.signing.otherSignersPending') : t('screens.signing.otherSignerPending')}
              </div>
            )}
          </div>
        </div>

        {/* PDF viewer */}
        {docUrl && (
          <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${BORD}`, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BORD}`, fontSize: 13, fontWeight: 600, color: DARK }}>
              {t('screens.signing.docPreviewHeader')}
            </div>
            <iframe
              src={docUrl}
              style={{ width: '100%', height: 480, border: 'none', display: 'block' }}
              title={t('screens.signing.docToSignAlt')}
            />
          </div>
        )}

        {/* Signature area */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: `1px solid ${BORD}`, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: DARK, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 16 }}>
            {t('screens.signing.yourSignature')}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: `1px solid ${BORD}`, marginBottom: 16, width: 'fit-content' }}>
            {[['draw', t('screens.signing.tabDraw')], ['type', t('screens.signing.tabType')], ['upload', t('screens.signing.tabUpload')]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => { setSigTab(key); setSigData(null); }}
                style={{
                  padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: sigTab === key ? BURG : '#fff',
                  color: sigTab === key ? '#fff' : MUTED,
                  borderRight: key !== 'upload' ? `1px solid ${BORD}` : 'none',
                  textTransform: 'capitalize',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {sigTab === 'draw'   && <DrawTab   onCapture={captureCallback} clearLabel={t('screens.signing.clear')} />}
          {sigTab === 'type'   && <TypeTab   onCapture={captureCallback} placeholder={t('screens.signing.typePh')} />}
          {sigTab === 'upload' && <UploadTab onCapture={captureCallback} ctaLabel={t('screens.signing.uploadCta')} />}

          {sigData && (
            <div style={{ marginTop: 12, padding: 12, background: BG, borderRadius: 10, border: `1px solid ${BORD}` }}>
              <div style={{ fontSize: 11, color: MUTED, marginBottom: 6 }}>{t('screens.signing.signaturePreview')}</div>
              <img src={sigData} alt={t('screens.signing.signaturePreviewAlt')} style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
            </div>
          )}
        </div>

        {/* Consent checkbox */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: `1px solid ${BORD}`, marginBottom: 24 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={e => setConsentChecked(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, accentColor: BURG, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, color: DARK, lineHeight: 1.7 }}>
              {t('screens.signing.consentText')}
            </span>
          </label>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => setShowDecline(d => !d)}
            style={{
              padding: '12px 24px', background: '#fff', border: `1px solid ${BORD}`,
              borderRadius: 10, fontSize: 14, fontWeight: 500, color: '#ef4444', cursor: 'pointer',
            }}
          >
            {t('screens.signing.declineToSign')}
          </button>
          <button
            onClick={handleSign}
            disabled={!sigData || !consentChecked || submitting}
            style={{
              padding: '12px 32px', background: BURG, border: 'none',
              borderRadius: 10, fontSize: 15, fontWeight: 700, color: '#fff',
              cursor: (!sigData || !consentChecked || submitting) ? 'not-allowed' : 'pointer',
              opacity: (!sigData || !consentChecked || submitting) ? 0.5 : 1,
            }}
          >
            {submitting ? t('screens.signing.signing') : t('screens.signing.signDocument')}
          </button>
        </div>

        {/* Decline form */}
        {showDecline && (
          <div style={{ marginTop: 20, background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #fecaca' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 10 }}>{t('screens.signing.declineReasonTitle')}</div>
            <textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              rows={3}
              placeholder={t('screens.signing.declineReasonPh')}
              style={{ width: '100%', padding: '10px 14px', border: `1px solid ${BORD}`, borderRadius: 10, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
            />
            <button
              onClick={handleDecline}
              disabled={submitting}
              style={{ marginTop: 12, padding: '10px 24px', background: '#ef4444', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#fff', cursor: submitting ? 'default' : 'pointer' }}
            >
              {submitting ? t('screens.signing.declining') : t('screens.signing.confirmDecline')}
            </button>
          </div>
        )}

        {/* Legal footer */}
        <div style={{ marginTop: 32, padding: '16px 20px', background: '#fff', borderRadius: 12, border: `1px solid ${BORD}`, fontSize: 11, color: MUTED, lineHeight: 1.6, textAlign: 'center' }}>
          {t('screens.signing.legalFooter')}
        </div>
      </div>
    </div>
  );
};

export default SigningScreen;
